import { Inject, Injectable, OnDestroy } from '@angular/core';
import { XsI18nPipe } from '@nx-fe/pipes';
import { BroadcastChannels, BroadcastService } from 'app/utils/broadcastService';
import { Subject } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';
import { McRcEventsService, SotiAssistStatus } from '../../remote-control-ng';
import { FileManagerService } from './file-manager.service';
import { FileDownloadService } from '../../remote-control-ng/services/downloadService';
import { RcContext } from '../../remote-control-ng/context/rcContext';
import { AgentDumpLogMessage } from '../../remote-control-ng/messages/pocket-com';
import { AgentDumpLogMessageType } from '../../remote-control-ng/definitions';
import { DeviceLogsRequestCancelled } from '../../remote-control-ng/messages/xsight';

export enum DeviceLogRequestStatus {
  None = 0, // There is no Device Logs Request
  Pending, // We sent the request to the server, user cannot cancelit
  Delayed, // When the device log request is delayed, we allow the user to cancel it
  Attaching, // We are attaching the Activity, User cannot cancel it
  Error, // There was an error downloading the device logs
}

export interface IDeviceLogConfig {
  reportType: number;
  dumpLogsCategories: string;
}

export interface IDeviceLogRequestStatusChange {
  previous: DeviceLogRequestStatus;
  current: DeviceLogRequestStatus;
  message: string; // If present, should be the message label that should be localized before showing to the client
}

@Injectable({
  providedIn: 'root',
})
export class RcLogsService implements OnDestroy {
  private readonly deviceLogRequestDelayed = 5000;
  private readonly deviceLogRequestStatusChangedBroadcast: BroadcastService<IDeviceLogRequestStatusChange> =
    new BroadcastService(BroadcastChannels.onDeviceLogRequestStatusChanged);

  private deviceLogRequestStatus = DeviceLogRequestStatus.None;
  private deviceLogUserPref: IDeviceLogConfig;
  private deviceLogsCategoriesCache: string[];

  private onDestroy$ = new Subject<void>();

  constructor(
    // private rcUserPreferencesService: RemoteControlUserPreferencesService,
    private rcEventsService: McRcEventsService,
    private fileManagerService: FileManagerService,
    private saI18nPipe: XsI18nPipe,
    private downloadService: FileDownloadService
  ) {
    this.deviceLogRequestStatusChangedBroadcast.onMessage = this.handleDeviceLogsDownloadStatusChange;

    this.rcEventsService.rcDeviceLogsDownloadReady
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(this.onDeviceLogsDownloadReady);

    this.rcEventsService.rcServerStatusUpdate.pipe(takeUntil(this.onDestroy$)).subscribe(this.onServerStatusUpdate);
  }

  ngOnDestroy(): void {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }

  public resetPrefs() {
    this.deviceLogsCategoriesCache = undefined;
    this.deviceLogUserPref = undefined;
  }

  /**
   * Downloads the specified list of device logs categories, or all of them, if the list is not provided
   *
   * @param deviceLogCategories Optional list og categories to be downloaded
   */
  public downloadDeviceLogs = (deviceLogCategories: Array<string> = null) => {
    console.info('Downloading logs:', deviceLogCategories);

    if (this.getDeviceLogRequestStatus() !== DeviceLogRequestStatus.None) {
      console.error('Logs cannot be downloaded now as there is a pending download device logs request');
      return;
    }

    if (deviceLogCategories && deviceLogCategories.length > 0) {
      const message = new AgentDumpLogMessage(AgentDumpLogMessageType.MC_DEBUG_REPORT_BY_LIST, deviceLogCategories);
      RcContext.instance.session.sendCallbackRequest(message);

      // Save the settings so next time we remember user selection...
      this.saveDeviceLogsCategoriesSettings(deviceLogCategories);
    } else {
      const message = new AgentDumpLogMessage(AgentDumpLogMessageType.PC_AGENT_DUMP_LOG_REQUEST);
      RcContext.instance.session.sendCallbackRequest(message);
    }
    this.setDeviceLogRequestStatus(DeviceLogRequestStatus.Pending);
  };

  public cancelDeviceLogsRequest = (): void => {
    RcContext.instance.session.sendCallbackRequest(new DeviceLogsRequestCancelled());
    this.setDeviceLogRequestStatus(DeviceLogRequestStatus.None);
  };

  public getDeviceLogRequestStatus = (): DeviceLogRequestStatus => this.deviceLogRequestStatus;

  public setDeviceLogRequestStatus = (status: DeviceLogRequestStatus, message: string = null): void => {
    if (this.deviceLogRequestStatus !== status) {
      if (status === DeviceLogRequestStatus.Pending) {
        setTimeout(() => {
          // Enable user to cancel the device logs request if it is taking too long to complete
          if (this.getDeviceLogRequestStatus() === DeviceLogRequestStatus.Pending) {
            this.setDeviceLogRequestStatus(DeviceLogRequestStatus.Delayed);
          }
        }, this.deviceLogRequestDelayed);
      }
      const oldStatus = this.deviceLogRequestStatus;
      this.deviceLogRequestStatus = status;

      if (status === DeviceLogRequestStatus.None || status === DeviceLogRequestStatus.Error) {
        this.fileManagerService.resumeQueue();
      } else if (status === DeviceLogRequestStatus.Pending) {
        this.fileManagerService.pauseQueue();
      }
      // Post a message to the channel, so everyone, (on any window) subscribed to this channel receives it
      this.deviceLogRequestStatusChangedBroadcast.postMessage({
        previous: oldStatus,
        current: this.deviceLogRequestStatus,
        message,
      });
    }
  };

  public saveDeviceLogsCategoriesSettings = (categories: Array<string>) => {
    // Save Device Category User Pereferences
    if (this.deviceLogUserPref) {
      this.deviceLogUserPref.dumpLogsCategories = categories.join(', ');
    }
    // this.rcUserPreferencesService.updateDeviceLogTypeConfig(this.deviceLogUserPref).subscribe(result => {
    // });
  };

  // TODO check agent verstion < support Dump Logs Categories

  // Agent version < 14.0.0                       => download logs not supported
  // Agent version  > 14.0.0 and < 14.2.3.1070    => download old device logs
  // Agent version  > 14.2.3.1070                 => show popup and download logs by category
  public requestDeviceLogsCategories = (): Promise<Map<string, boolean>> => {
    let deviceLogCategories: string[];
    let selectedDeviceLogCategories: string[];

    return this.getDeviceLogsCategories()
      .then(logCategories => {
        deviceLogCategories = logCategories;
        return this.getSelectedDeviceLogCategories();
      })
      .then(selectedLogCategories => {
        selectedDeviceLogCategories = selectedLogCategories;

        return this.matchDeviceLogCategories(selectedDeviceLogCategories, deviceLogCategories);
      });
  };

  public isDownloadLogsButtonSpinning = (): boolean => this.getDeviceLogRequestStatus() !== DeviceLogRequestStatus.None;

  public isDownloadLogsButtonEnabled = (): boolean =>
    this.getDeviceLogRequestStatus() === DeviceLogRequestStatus.None ||
    this.getDeviceLogRequestStatus() === DeviceLogRequestStatus.Delayed;

  public getDownloadDeviceLogsIcon = (): string => {
    const deviceLogRequestStatus = this.getDeviceLogRequestStatus();
    let iconResult;

    switch (deviceLogRequestStatus) {
      case DeviceLogRequestStatus.Delayed:
        iconResult = 'hd-icon-close';
        break;
      default:
        iconResult = 'hd-icon-downloadlogs';
        break;
    }

    return iconResult;
  };

  public getDownloadLogButtoncssClass = (): string => {
    let cssClass = '';

    if (this.isDownloadLogsButtonSpinning()) {
      cssClass = 'spinning ';
    }
    if (this.isDownloadLogsButtonEnabled()) {
      cssClass += 'enabled';
    }

    return cssClass;
  };

  public getDownloadLogButtonTooltip = (): string => {
    let tooltip = '';
    if (this.fileManagerService.fileTransferInProgress()) {
      tooltip = this.saI18nPipe.transform('ticket_RemoteControlTabs_DeviceLogsDisabled');
    } else {
      tooltip =
        this.getDeviceLogRequestStatus() !== DeviceLogRequestStatus.Delayed
          ? this.saI18nPipe.transform('ticket_RemoteControlTabs_DownloadDeviceLogs')
          : this.saI18nPipe.transform('ticket_RemoteControlTabs_DownloadDeviceLogsCancelTooltip');
    }
    return tooltip;
  };

  /** Gets the Available Device Log categories from the agent */
  private getDeviceLogsCategories = (): Promise<string[]> =>
    new Promise<string[]>((resolve, reject) => {
      try {
        if (this.deviceLogsCategoriesCache) {
          console.log('DeviceLogsCategories cached:', this.deviceLogsCategoriesCache);
          resolve(this.deviceLogsCategoriesCache);
        } else {
          // Get the available log categories from the agent...
          const message = new AgentDumpLogMessage(AgentDumpLogMessageType.MC_DEBUG_REPORT_QUERY);
          RcContext.instance.session.sendRequest(message)
            .pipe(take(1), takeUntil(this.onDestroy$))
            .subscribe((reply: AgentDumpLogMessage) => {
              console.log('DeviceLogsCategories from Agent:', reply.categories);
              this.deviceLogsCategoriesCache = reply.categories;
              resolve(reply.categories);
            },
            (error) => {
              console.error('Error requesting AgentDumpLogsCategories', error);
              reject(error);      
            });
        }
      } catch (error) {
        console.error('Error requesting AgentDumpLogsCategories', error);
        reject(error);
      }
    });

  /** Gets the Selected Device Log categories from stored settings */
  private getSelectedDeviceLogCategories = (): Promise<string[]> =>
    new Promise<string[]>((resolve, reject) => {
      try {
        if (this.deviceLogUserPref) {
          resolve(
            this.deviceLogUserPref.dumpLogsCategories
              .split(',') // Get the categories list...
              .map(c => c.trim())
          );
        } else {
          resolve([]);
          //XTODO
          // return this.rcUserPreferencesService.getDeviceLogTypeConfig().toPromise()
          //     .then((deviceLogsConfig: IDeviceLogConfig) => {

          //         console.log('DeviceLogsCategories from Server:', deviceLogsConfig.dumpLogsCategories);

          //         // Update the local copy of the device logs config settings..
          //         this.deviceLogUserPref = deviceLogsConfig;

          //         // Now create the map merging the agent categories with the server device log settings...
          //         let userPrefCategories: string[] = [];
          //         if (deviceLogsConfig.dumpLogsCategories !== '') {
          //             userPrefCategories = deviceLogsConfig.dumpLogsCategories
          //                 .split(',')                 // Get the categories list...
          //                 .map(c => c = c.trim());    // Trim each item in the list...
          //         }
          //         resolve(userPrefCategories);

          //     }).catch((error: any) => {
          //         console.error('Error getting log settings from server:', error);
          //         reject(error);
          //     });
        }
      } catch (error) {
        console.error('Error getting log settings from server:', error);
        reject(error);
      }
    });

  // Match previously selected log categories to what's available
  private matchDeviceLogCategories = (
    prevCategories: string[],
    availableCategories: string[]
  ): Map<string, boolean> => {
    const checklist = new Map();
    const selectCategories = prevCategories.map(name => name.toLowerCase());

    // If there are no preselected categories, select "Logs" by default
    if (selectCategories.length === 0) {
      selectCategories.push('logs');
    }

    // Get the intersection of device available categories and usersettings categories
    availableCategories.forEach(element => {
      const selected = selectCategories.includes(element.toLowerCase());
      checklist.set(element, selected);
    });

    return checklist;
  };

  private handleDeviceLogsDownloadStatusChange = (statusChange: IDeviceLogRequestStatusChange): void => {
    this.deviceLogRequestStatus = statusChange.current;
  };

  private onDeviceLogsDownloadReady = (event: any): void => {
    const attachmentUri = event.deviceLogsDownloadReadyDetails.attachmentLink;
    const attachmentName = event.deviceLogsDownloadReadyDetails.fileName;

    let success = true;
    try {
      this.downloadService.triggerDownloadLink(attachmentUri, attachmentName);
    } catch {
      success = false;
    }

    this.setDeviceLogRequestStatus(success ? DeviceLogRequestStatus.None : DeviceLogRequestStatus.Error);
  };

  private onServerStatusUpdate = (onServerStatusUpdate: any): void => {
    console.info('Server Status Update:', onServerStatusUpdate);

    if (RcContext.instance.serverStatus?.status & SotiAssistStatus.SasLogFileUnavailableOnAgent) {
      this.setDeviceLogRequestStatus(DeviceLogRequestStatus.Error, 'remoteControl_DeviceLogsFileUnavailableInAgent');
    }
  };
}
